import { useEffect, useRef } from 'react';
import { useReducedMotion } from 'motion/react';

/**
 * One animated dot's spec:
 *  - `path`  : SVG path-data string the dot rides (in the SVG's 428×494 viewBox).
 *  - `duration`: seconds per leg (yoyo) or per full loop (loop).
 *  - `mode`  : 'loop' walks 0→1→0 around the path forever (closed shapes); 'yoyo'
 *              eases 0→1, then 1→0 with sine.inOut (open lines), forever.
 *  - `reverse`: yoyo only — start at path end and go backward first.
 *
 * The path strings are lifted from each control-hub graphic component
 * (PlayerGraphics/*Graphic.jsx). The static dots that previously sat at these
 * paths' anchor points have been removed from each `{Player}_pieces.svg`, so
 * the only visible dots on the rendered hex are the animated ones driven from
 * here.
 */
export type DotAnim = {
  path: string;
  duration: number;
  mode: 'loop' | 'yoyo';
  reverse?: boolean;
};

export type PlayerHexConfig = {
  slug: string;
  name: string;
  photo: string;
  pieces: string;
  dots: DotAnim[];
};

const DOT_RADIUS = 5.07;

export function PlayerHex({ player, className }: { player: PlayerHexConfig; className?: string }) {
  return (
    <div
      className={`relative aspect-[428/494] w-full ${className ?? ''}`.trim()}
      data-player-slug={player.slug}
    >
      <img
        src={player.photo}
        alt={player.name}
        className="pointer-events-none absolute inset-0 h-full w-full select-none object-cover object-center"
        loading="lazy"
        decoding="async"
      />
      <img
        src={player.pieces}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full select-none"
        loading="lazy"
        decoding="async"
      />
      <svg
        aria-hidden="true"
        viewBox="0 0 428 494"
        preserveAspectRatio="xMidYMid meet"
        className="pointer-events-none absolute inset-0 h-full w-full"
      >
        {player.dots.map((dot, i) => (
          <AnimatedDot key={i} dot={dot} />
        ))}
      </svg>
    </div>
  );
}

function AnimatedDot({ dot }: { dot: DotAnim }) {
  const pathRef = useRef<SVGPathElement>(null);
  const circleRef = useRef<SVGCircleElement>(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const pathEl = pathRef.current;
    const circleEl = circleRef.current;
    if (!pathEl || !circleEl) return;

    const totalLen = pathEl.getTotalLength();

    // Place at frame 0 immediately so the dot doesn't pop in at (0,0).
    const initialProgress = dot.mode === 'yoyo' && dot.reverse ? 1 : 0;
    const p0 = pathEl.getPointAtLength(initialProgress * totalLen);
    circleEl.setAttribute('cx', String(p0.x));
    circleEl.setAttribute('cy', String(p0.y));

    if (reduce) return;

    let raf = 0;
    const t0 = performance.now() / 1000;

    const tick = () => {
      const elapsed = performance.now() / 1000 - t0;
      let progress: number;
      if (dot.mode === 'loop') {
        // ease: none, infinite loop around closed path
        progress = (elapsed / dot.duration) % 1;
      } else {
        // yoyo: linear time 0→2 → leg 0→1→0 → eased sine.inOut → progress
        const cyclePos = (elapsed / dot.duration) % 2;
        const leg = cyclePos < 1 ? cyclePos : 2 - cyclePos;
        progress = 0.5 - 0.5 * Math.cos(leg * Math.PI);
        if (dot.reverse) progress = 1 - progress;
      }
      const pt = pathEl.getPointAtLength(progress * totalLen);
      circleEl.setAttribute('cx', String(pt.x));
      circleEl.setAttribute('cy', String(pt.y));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [dot.path, dot.duration, dot.mode, dot.reverse, reduce]);

  return (
    <>
      <path ref={pathRef} d={dot.path} fill="none" stroke="none" pointerEvents="none" opacity={0} />
      <circle
        ref={circleRef}
        r={DOT_RADIUS}
        fill="white"
        style={{ filter: 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.9))' }}
      />
    </>
  );
}
